import { useForm } from '@tanstack/react-form'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'

interface FormData {
  fullname: string
  email: string
  schoolname: string
  propertyType: string
  phonenumber: string
  numberOfStudents: string
  numberOfTeachers: string
  painProblems: string[]
  painProblemOther: string
  currentTools: string
  requestdate: string
}

export const Route = createFileRoute('/join')({
  component: JoinForm,
})

function JoinForm(): JSX.Element {
  const { t } = useTranslation()
  const [isSuccess, setIsSuccess] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(() => {
    const attempts = parseInt(localStorage.getItem('jeridschool_join_form_attempts') || '0', 10)
    return attempts >= 3
  })
  const [, setAttemptCount] = useState(() => {
    return parseInt(localStorage.getItem('jeridschool_join_form_attempts') || '0', 10)
  })
  
  // Format today's date as dd/mm/yyyy
  const getTodayDate = (): string => {
    const today = new Date()
    const day = String(today.getDate()).padStart(2, '0')
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const year = today.getFullYear()
    return `${day}/${month}/${year}`
  }
  
  const form = useForm({
    defaultValues: {
      fullname: '',
      email: '',
      schoolname: '',
      propertyType: 'private',
      phonenumber: '',
      numberOfStudents: '',
      numberOfTeachers: '',
      painProblems: [] as string[],
      painProblemOther: '',
      currentTools: '',
      requestdate: getTodayDate(),
    },
    onSubmit: async ({ value }: { value: FormData }) => {
      // Check if already submitted 3 times
      const currentAttempts = parseInt(localStorage.getItem('jeridschool_join_form_attempts') || '0', 10)
      if (currentAttempts >= 3) {
        alert(t('routes.join.alerts.maxAttemptsReached'))
        return
      }

      // Validate all required fields are not empty
      if (!value.fullname.trim()) {
        alert(t('routes.join.alerts.fullnameRequired'))
        return
      }
      if (!value.email.trim()) {
        alert(t('routes.join.alerts.emailRequired'))
        return
      }
      if (!value.schoolname.trim()) {
        alert(t('routes.join.alerts.schoolnameRequired'))
        return
      }
      if (!value.propertyType) {
        alert(t('routes.join.alerts.propertyTypeRequired'))
        return
      }
      if (!value.phonenumber.trim()) {
        alert(t('routes.join.alerts.phonenumberRequired'))
        return
      }
      if (value.phonenumber.length !== 8) {
        alert(t('routes.join.alerts.phonenumberInvalid'))
        return
      }
      if (!value.numberOfStudents.trim()) {
        alert(t('routes.join.alerts.numberOfStudentsRequired'))
        return
      }
      if (!value.numberOfTeachers.trim()) {
        alert(t('routes.join.alerts.numberOfTeachersRequired'))
        return
      }
      if (value.painProblems.length === 0) {
        alert(t('routes.join.alerts.painProblemsRequired'))
        return
      }
      if (value.painProblems.includes('Other') && !value.painProblemOther.trim()) {
        alert(t('routes.join.alerts.painProblemOtherRequired'))
        return
      }
      if (!value.currentTools.trim()) {
        alert(t('routes.join.alerts.currentToolsRequired'))
        return
      }

      try {
        // Always get today's date at submission time
        const todayDate = getTodayDate()
        
        const dataToSubmit = {
          data: [
            {
              fullname: value.fullname,
              email: value.email,
              schoolname: value.schoolname,
              propertyType: value.propertyType,
              phonenumber: value.phonenumber,
              numberOfStudents: value.numberOfStudents,
              numberOfTeachers: value.numberOfTeachers,
              painProblems: value.painProblems.join(', '),
              painProblemOther: value.painProblemOther,
              currentTools: value.currentTools,
              requestdate: todayDate,
            },
          ],
        }

        const response = await fetch(
          'https://sheetdb.io/api/v1/b81ea9nhoaxhx',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(dataToSubmit),
          }
        )

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || t('routes.join.alerts.error'))
        }

        const result = await response.json()
        console.log('Form submitted successfully:', result)
        
        // Save submission flag to localStorage
        localStorage.setItem('jeridschool_join_form_submitted', 'true')
        setHasSubmitted(true)
        setIsSuccess(true)
        form.reset()
        
        // Increment submission attempts in localStorage
        const newAttemptCount = currentAttempts + 1
        localStorage.setItem('jeridschool_join_form_attempts', newAttemptCount.toString())
        setAttemptCount(newAttemptCount)
        if (newAttemptCount >= 3) {
          setHasSubmitted(true)
        }

        // Reset success state after 3 seconds
        setTimeout(() => setIsSuccess(false), 3000)
      } catch (error) {
        // console.error('Submission error:', error)
        alert(t('routes.join.alerts.error'))
      }
    },
  })

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('routes.join.alerts.success')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('routes.join.success.thankYou')}
          </p>
          <button
            onClick={() => setIsSuccess(false)}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200"
          >
            {t('routes.join.form.buttons.submitAnother')}
          </button>
        </div>
      </div>
    )
  }

  const painProblemOptions = [
    { key: 'timetableCreation', value: 'Timetable creation' },
    { key: 'teacherStaffManagement', value: 'Teacher/Staff management' },
    { key: 'studentManagementAttendance', value: 'Student management & attendance' },
    { key: 'communicationWithParents', value: 'Communication with parents' },
    { key: 'examsGrading', value: 'Exams & grading' },
    { key: 'roomResourceScheduling', value: 'Room/Resource scheduling' },
    { key: 'administrativePaperwork', value: 'Administrative paperwork' },
    { key: 'finance', value: 'Finance' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header with Logo */}
        <div className="p-8 text-center border-b border-gray-100">
          {/* Logo */}
          <div className="flex items-center justify-center mb-6">
            <img 
              src="/assets/logo.png" 
              alt="JeridSchool Logo" 
              className="w-24 h-24 mr-3"
            />
            <div className="text-left">
              <h1 className="text-2xl font-bold text-gray-900">JeridSchool</h1>
              <p className="text-sm text-gray-500">{t('routes.join.community')}</p>
            </div>
          </div>
          
          <h2 className="text-3xl font-semibold text-gray-900 mb-2">
            {t('routes.join.title')}
          </h2>
          <p className="text text-gray-600 text-base">
            {t('routes.join.description')}
          </p>
        </div>

        {/* Form */}
        <div className="p-8">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              void form.handleSubmit()
            }}
            className="space-y-6"
          >
            {/* Full Name Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('routes.join.form.inputs.fullname.label')}
              </label>
              <form.Field name="fullname">
                {(field) => (
                  <input
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200"
                    placeholder={t('routes.join.form.inputs.fullname.placeholder')}
                    required
                  />
                )}
              </form.Field>
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('routes.join.form.inputs.email.label')}
              </label>
              <form.Field name="email">
                {(field) => (
                  <input
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    type="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200"
                    placeholder={t('routes.join.form.inputs.email.placeholder')}
                    required
                  />
                )}
              </form.Field>
            </div>

            {/* School Name Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('routes.join.form.inputs.schoolname.label')}
              </label>
              <form.Field name="schoolname">
                {(field) => (
                  <input
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200"
                    placeholder={t('routes.join.form.inputs.schoolname.placeholder')}
                    required
                  />
                )}
              </form.Field>
            </div>

            {/* Property Type Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('routes.join.form.inputs.propertyType.label')}
              </label>
              <form.Field name="propertyType">
                {(field) => (
                  <select
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200 bg-white"
                    required
                  >
                    <option value="private">{t('routes.join.form.inputs.propertyType.options.private')}</option>
                    <option value="public">{t('routes.join.form.inputs.propertyType.options.public')}</option>
                  </select>
                )}
              </form.Field>
            </div>

            {/* Phone Number Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('routes.join.form.inputs.phonenumber.label')}
              </label>
              <form.Field name="phonenumber">
                {(field) => (
                  <>
                    <input
                      name={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => {
                        // Only allow numbers and limit to 8 digits
                        const value = e.target.value.replace(/\D/g, '').slice(0, 8)
                        field.handleChange(value)
                      }}
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      minLength={8}
                      maxLength={8}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200"
                      placeholder={t('routes.join.form.inputs.phonenumber.placeholder')}
                      required
                    />
                    {field.state.value && field.state.value.length !== 8 && (
                      <p className="text-xs text-red-500 mt-1">
                        {t('routes.join.form.inputs.phonenumber.error')}
                      </p>
                    )}
                  </>
                )}
              </form.Field>
            </div>

            {/* How many students */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('routes.join.form.inputs.numberOfStudents.label')}
              </label>
              <form.Field name="numberOfStudents">
                {(field) => (
                  <input
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/\D/g, '')
                      field.handleChange(value)
                    }}
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    min="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200"
                    placeholder={t('routes.join.form.inputs.numberOfStudents.placeholder')}
                    required
                  />
                )}
              </form.Field>
            </div>

            {/* How many teachers */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('routes.join.form.inputs.numberOfTeachers.label')}
              </label>
              <form.Field name="numberOfTeachers">
                {(field) => (
                  <input
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/\D/g, '')
                      field.handleChange(value)
                    }}
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    min="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200"
                    placeholder={t('routes.join.form.inputs.numberOfTeachers.placeholder')}
                    required
                  />
                )}
              </form.Field>
            </div>

            {/* What is the pain problem */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                {t('routes.join.form.inputs.painProblems.label')}
              </label>
              <form.Field name="painProblems">
                {(field) => (
                  <div className="space-y-2">
                    {painProblemOptions.map((option) => (
                      <label key={option.key} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={field.state.value.includes(option.value)}
                          onChange={(e) => {
                            const currentValue = field.state.value
                            if (e.target.checked) {
                              field.handleChange([...currentValue, option.value])
                            } else {
                              field.handleChange(currentValue.filter((v) => v !== option.value))
                            }
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{t(`routes.join.form.inputs.painProblems.options.${option.key}`)}</span>
                      </label>
                    ))}
                    <div className="mt-3">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={field.state.value.includes('Other')}
                          onChange={(e) => {
                            const currentValue = field.state.value
                            if (e.target.checked) {
                              field.handleChange([...currentValue, 'Other'])
                            } else {
                              field.handleChange(currentValue.filter((v) => v !== 'Other'))
                            }
                          }}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{t('routes.join.form.inputs.painProblems.options.other')}</span>
                      </label>
                      {field.state.value.includes('Other') && (
                        <form.Field name="painProblemOther">
                          {(otherField) => (
                            <input
                              name={otherField.name}
                              value={otherField.state.value}
                              onBlur={otherField.handleBlur}
                              onChange={(e) => otherField.handleChange(e.target.value)}
                              type="text"
                              className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200"
                              placeholder={t('routes.join.form.inputs.painProblems.otherPlaceholder')}
                            />
                          )}
                        </form.Field>
                      )}
                    </div>
                  </div>
                )}
              </form.Field>
            </div>

            {/* What tools or methods are you currently using */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('routes.join.form.inputs.currentTools.label')}
              </label>
              <p className="text-xs text-gray-500 mb-2 italic">{t('routes.join.form.inputs.currentTools.helperText')}</p>
              <form.Field name="currentTools">
                {(field) => (
                  <input
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    type="text"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors duration-200"
                    placeholder={t('routes.join.form.inputs.currentTools.placeholder')}
                    required
                  />
                )}
              </form.Field>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </span>
              </div>
            </div>

            {/* Already Submitted Message */}
            {hasSubmitted && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-yellow-800 text-center">
                  {t('routes.join.alerts.maxAttemptsReached')}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={form.state.isSubmitting || hasSubmitted}
              className="w-full flex justify-center items-center gap-2 py-3 px-6 border border-transparent rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors duration-200"
            >
              {form.state.isSubmitting ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('routes.join.form.buttons.submitting')}
                </>
              ) : (
                t('routes.join.form.buttons.submit')
              )}
            </button>

            {/* Warning Text */}
            <p className="text-xs text-gray-500 text-center mt-3">
              {t('routes.join.warning')}
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}

export default JoinForm
